import axios from "axios";
import { getToken, refreshTokens, handleAuthFailure } from "../utils/auth";
import * as Sentry from "@sentry/react";

if (process.env.REACT_APP_DOMAIN) {
  axios.defaults.baseURL = process.env.REACT_APP_DOMAIN;
}

let isRefreshing = false;
let failedQueue = [];
let refreshTimeout = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const resetRefreshState = () => {
  isRefreshing = false;
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
};

// Add a response interceptor
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Skip refresh logic for token endpoints - safe check for network errors
    if (error.request?.responseURL?.includes("/api/v2/token/")) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    // If the error status is 401
    // it means the token has expired and we need to refresh it
    if (error.response?.status === 401) {
      // Immediate concurrency protection - lock as soon as 401 is detected
      if (!isRefreshing) {
        isRefreshing = true;
      } else {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      // Check if this request already tried to refresh
      if (originalRequest._retry) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      // Check if tokens have been updated by another tab
      const { token: currentAccessToken } = getToken("access_token");
      const originalToken =
        originalRequest.headers?.Authorization?.split(" ")[1];

      if (currentAccessToken && currentAccessToken !== originalToken) {
        isRefreshing = false;
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${currentAccessToken}`;
        return axios(originalRequest);
      }

      // At this point, we're committed to refreshing
      originalRequest._retry = true;

      // Set timeout for refresh process with race condition protection
      refreshTimeout = setTimeout(() => {
        if (isRefreshing) {
          processQueue(new Error("Token refresh timeout"), null);
          localStorage.clear();
          sessionStorage.clear();
          window.dispatchEvent(new Event("storage"));
          window.location.href = "/login";
          resetRefreshState();
        }
      }, 10000);

      try {
        // Use shared token refresh logic
        const access = await refreshTokens();

        axios.defaults.headers.common["Authorization"] = `Bearer ${access}`;

        processQueue(null, access);
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Use shared auth failure handler
        handleAuthFailure();

        return Promise.reject(refreshError);
      } finally {
        resetRefreshState();
      }
    }

    // Capture non-401 API errors in Sentry
    if (error.response) {
      Sentry.captureException(error, {
        tags: {
          "api.endpoint": error.config?.url || "unknown",
          "api.status": error.response.status,
          "api.method": error.config?.method?.toUpperCase() || "unknown",
        },
        extra: {
          responseData: error.response.data,
          requestData: error.config?.data,
        },
      });
    }

    return Promise.reject(error);
  }
);

// Add a request interceptor
axios.interceptors.request.use(function (config) {
  const { token } = getToken("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add Sentry breadcrumb for API requests
  Sentry.addBreadcrumb({
    category: "api",
    message: `${config.method?.toUpperCase()} ${config.url}`,
    level: "info",
    data: {
      url: config.url,
      method: config.method,
    },
  });

  return config;
});

